import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Brain, Check, X } from 'lucide-react';

import { Button } from '@accio3d/design-system/button';

import { useApiServices } from '../../api/services';
import { useAuth } from '../../hooks/useAuth';
import { agentMemoriesKey } from './agent-query-keys';

export function AgentProposedMemory() {
  const { agent } = useApiServices();
  const { organization } = useAuth();
  const queryClient = useQueryClient();
  const key = agentMemoriesKey(organization?.id);

  const { data: memories } = useQuery({
    queryKey: key,
    queryFn: () => agent.listMemories(),
    enabled: !!organization?.id,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: key });

  const confirm = useMutation({
    mutationFn: (id: string) => agent.confirmMemory(id),
    onSuccess: invalidate,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => agent.forgetMemory(id),
    onSuccess: invalidate,
  });

  const pending = (memories ?? []).filter((memory) => !memory.confirmedAt);
  const proposed = pending[0];
  if (!proposed) return null;

  const busy = confirm.isPending || dismiss.isPending;

  return (
    <div className="border-t border-border/50 px-3 py-2">
      <div className="flex items-start gap-1.5">
        <Brain className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">
            Remember this{proposed.scope === 'organization' ? ' for your team' : ''}?
          </p>
          <p className="text-xs wrap-break-word">{proposed.fact}</p>
        </div>
        <Button
          size="icon"
          variant="ghost"
          title="Remember this"
          aria-label={`Remember: ${proposed.fact}`}
          className="h-6 w-6 shrink-0"
          disabled={busy}
          onClick={() => confirm.mutate(proposed.id)}
        >
          <Check className="h-3 w-3" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          title="Do not remember this"
          aria-label={`Discard: ${proposed.fact}`}
          className="h-6 w-6 shrink-0 text-muted-foreground"
          disabled={busy}
          onClick={() => dismiss.mutate(proposed.id)}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
      {pending.length > 1 && (
        <p className="mt-1 pl-4.5 text-xs text-muted-foreground">
          {pending.length - 1} more waiting under “What I remember”.
        </p>
      )}
    </div>
  );
}
